import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import {
  Save, Plus, ChevronLeft, ChevronRight, Trash2, Zap, MapPin,
  RefreshCw, Bell, Calendar as CalendarIcon,
  Info, Lock, Settings2
} from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { PageHeader } from '../components/common/PageHeader';
import { attendanceService } from '../services/index';
import { employeeService } from '../services/employee.service';
import apiClient from '../services/apiClient';
import { selectUser } from '../store/authSlice';
import { KEYS } from '../lib/queryKeys';
import { formatINR } from '../lib/format';

// ── API helpers ───────────────────────────────────────────────────────────────
const leaveApi = {
  getAll: (year) => apiClient.get('/api/leave/all', { params: { year } }).then(r => r.data),
};
const holidayApi = {
  get:             (year)          => apiClient.get('/api/holidays', { params: { year } }).then(r => r.data),
  add:             (d)             => apiClient.post('/api/holidays', d).then(r => r.data),
  remove:          (date)          => apiClient.delete(`/api/holidays/${date}`).then(r => r.data),
  seedNational:    (year)          => apiClient.post('/api/holidays/seed/national', {}, { params: { year } }).then(r => r.data),
  seedState:       (year)          => apiClient.post('/api/holidays/seed/state', {}, { params: { year } }).then(r => r.data),
  toggleMandatory: (year, id, val) => apiClient.patch(`/api/holidays/${year}/holidays/${id}/toggle`, { isMandatory: val }).then(r => r.data),
};
const settingsApi = {
  get:    ()  => apiClient.get('/api/settings/business').then(r => r.data),
  update: (d) => apiClient.patch('/api/settings/business', d).then(r => r.data),
};

const WEEKDAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS   = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  present:   { label: 'Present',  short: 'P', bg: 'bg-emerald-500', light: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  absent:    { label: 'Absent',   short: 'A', bg: 'bg-red-500',     light: 'bg-red-50 border-red-200 text-red-700'             },
  'half-day':{ label: 'Half Day', short: 'H', bg: 'bg-amber-400',   light: 'bg-amber-50 border-amber-200 text-amber-700'       },
  leave:     { label: 'Leave',    short: 'L', bg: 'bg-blue-500',    light: 'bg-blue-50 border-blue-200 text-blue-700'          },
};

// ── Leave type display — short badge shown on cell ────────────────────────────
const LEAVE_TYPE_SHORT = { PL: 'PL', CL: 'CL', SL: 'SL', LOP: 'LOP' };
const LEAVE_TYPE_COLOR = {
  PL:  'text-emerald-200',
  CL:  'text-blue-200',
  SL:  'text-sky-200',
  LOP: 'text-red-200',
};

const LEAVE_TYPES = [
  { value: 'PL',  label: 'Privilege Leave', sub: 'Paid' },
  { value: 'CL',  label: 'Casual Leave',    sub: 'Paid' },
  { value: 'SL',  label: 'Sick Leave',      sub: 'Paid' },
  { value: 'LOP', label: 'Loss of Pay',     sub: 'Unpaid' },
];

const TYPE_STYLE = {
  national: { label: 'National', cls: 'bg-blue-50 text-blue-700 border-blue-200',      dot: 'bg-blue-400'   },
  regional: { label: 'State',    cls: 'bg-purple-50 text-purple-700 border-purple-200', dot: 'bg-purple-400' },
  company:  { label: 'Company',  cls: 'bg-slate-50 text-slate-600 border-slate-200',    dot: 'bg-slate-400'  },
};

const addNotification = (msg) => {
  try {
    const list = JSON.parse(localStorage.getItem('ss_notifications') || '[]');
    list.unshift({ id: Date.now(), msg, ts: new Date().toISOString(), read: false });
    localStorage.setItem('ss_notifications', JSON.stringify(list.slice(0, 50)));
  } catch {}
};

export default function Attendance() {
  const qc   = useQueryClient();
  const user = useSelector(selectUser);
  const now  = new Date();

  const [empId,       setEmpId]       = useState('');
  const [year,        setYear]        = useState(now.getFullYear());
  const [month,       setMonth]       = useState(now.getMonth() + 1);
  const [localDays,   setLocalDays]   = useState({});
  const [leaveTypes,  setLeaveTypes]  = useState({});
  const [overtime,    setOvertime]    = useState({});
  const [dayModal,    setDayModal]    = useState(null);
  const [holModal,    setHolModal]    = useState(false);
  const [newHol,      setNewHol]      = useState({ name:'', date:'', type:'company', isMandatory:true });
  const [seedConfirm, setSeedConfirm] = useState(null);
  const [notifModal,  setNotifModal]  = useState(null);

  // ── Business settings (sunday working toggle) ─────────────────────────────
  const { data: settingsData, refetch: refetchSettings } = useQuery({
    queryKey: ['businessSettings'],
    queryFn:  settingsApi.get,
  });
  const sundayWorking = settingsData?.data?.businessConfig?.sundayWorking ?? false;

  const updateSettingsMut = useMutation({
    mutationFn: (d) => settingsApi.update(d),
    onSuccess: () => {
      toast.success(sundayWorking ? 'Sunday marked as day off' : 'Sunday enabled as working day');
      refetchSettings();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to update settings'),
  });

  // ── Employees ─────────────────────────────────────────────────────────────
  const { data: empData } = useQuery({
    queryKey: KEYS.employees({ status:'active' }),
    queryFn:  () => employeeService.list({ status:'active' }),
  });
  const employees   = empData?.data || [];
  const selectedId  = empId || employees[0]?._id || '';
  const selectedEmp = employees.find(e => e._id === selectedId);

  // ── Attendance data ───────────────────────────────────────────────────────
  const { data: attData, isFetching, isLoading: attLoading } = useQuery({
    queryKey: KEYS.attendance(selectedId, month, year),
    queryFn:  () => attendanceService.get(selectedId, month, year),
    enabled:  !!selectedId,
  });
  const attDoc   = attData?.data;
  const isLocked = attDoc?.isLocked || false;

  useEffect(() => {
    const s = {}, l = {}, o = {};
    (attDoc?.days || []).forEach(d => {
      s[d.date] = d.status;
      if (d.leaveType)     l[d.date] = d.leaveType;
      if (d.overtimeHours) o[d.date] = d.overtimeHours;
    });
    setLocalDays(s); setLeaveTypes(l); setOvertime(o);
  }, [attDoc, selectedId, month, year]);

  const saveMut = useMutation({
    mutationFn: (p) => attendanceService.save(p),
    onSuccess:  () => {
      toast.success('Attendance saved');
      qc.invalidateQueries({ queryKey: KEYS.attendance(selectedId, month, year) });
      qc.invalidateQueries({ queryKey: ['leave','all',year] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Save failed'),
  });

  const buildPayload = (days, lt, ot) => ({
    employeeId: selectedId, month, year,
    days: Object.keys(days).filter(d => days[d]).map(d => ({
      date: Number(d), status: days[d],
      leaveType: lt[d] || null, overtimeHours: ot[d] || 0,
    })),
  });

  const handleSave = () => saveMut.mutate(buildPayload(localDays, leaveTypes, overtime));

  const openDay = (day) => {
    const cellDate = new Date(year, month-1, day);
    const todayMid = new Date(); todayMid.setHours(0,0,0,0);
    if (cellDate < todayMid && !localDays[day]) return;
    setDayModal({ day, status: localDays[day]||'', leaveType: leaveTypes[day]||'', overtime: overtime[day]||0 });
  };

  const saveDay = () => {
    if (!dayModal) return;
    const { day, status, leaveType, overtime: ot } = dayModal;
    const nd = { ...localDays,  [day]: status };
    const nl = { ...leaveTypes, [day]: leaveType };
    const no = { ...overtime,   [day]: ot };
    setLocalDays(nd); setLeaveTypes(nl); setOvertime(no);
    setDayModal(null);
    saveMut.mutate(buildPayload(nd, nl, no));
  };

  const daysInMonth  = new Date(year, month, 0).getDate();
  const firstWeekday = new Date(year, month-1, 1).getDay();

  const summary = useMemo(() => {
    const c = { present:0, absent:0, 'half-day':0, leave:0, overtime:0 };
    Object.entries(localDays).forEach(([,s]) => { if (c[s]!==undefined) c[s]++; });
    Object.values(overtime).forEach(h => { c.overtime += h||0; });
    return { ...c, effective: c.present + c['half-day']*0.5 + c.leave };
  }, [localDays, overtime]);

  // ── Holidays ──────────────────────────────────────────────────────────────
  const { data: holData, isLoading: holLoading } = useQuery({
    queryKey: ['holidays', year],
    queryFn:  () => holidayApi.get(year),
  });
  const holidays   = holData?.data?.holidays || [];
  const holidayMap = useMemo(() => {
    const m = {};
    holidays.forEach(h => {
      const d = new Date(h.date);
      if (d.getFullYear()===year && d.getMonth()+1===month) m[d.getDate()] = h;
    });
    return m;
  }, [holidays, month, year]);

  // ── Leave balances ────────────────────────────────────────────────────────
  const { data: leaveData, isLoading: leaveLoading } = useQuery({
    queryKey: ['leave','all',year],
    queryFn:  () => leaveApi.getAll(year),
    retry: false,
  });
  const leaveBalances = leaveData?.data || [];

  const addHolMut = useMutation({
    mutationFn: (d) => holidayApi.add({ ...d, year }),
    onSuccess:  () => { toast.success('Holiday added'); qc.invalidateQueries({ queryKey:['holidays',year] }); setHolModal(false); setNewHol({name:'',date:'',type:'company',isMandatory:true}); },
    onError:    (e) => toast.error(e.response?.data?.message||'Failed'),
  });
  const removeHolMut = useMutation({
    mutationFn: (date) => holidayApi.remove(date),
    onSuccess:  () => { toast.success('Removed'); qc.invalidateQueries({ queryKey:['holidays',year] }); },
  });
  const toggleMandMut = useMutation({
    mutationFn: ({ id, val }) => holidayApi.toggleMandatory(year, id, val),
    onSuccess:  () => qc.invalidateQueries({ queryKey:['holidays',year] }),
  });
  const seedMut = useMutation({
    mutationFn: (type) => type==='national' ? holidayApi.seedNational(year) : holidayApi.seedState(year),
    onSuccess:  (r) => { toast.success(`Imported ${r.data?.added} holidays`); qc.invalidateQueries({ queryKey:['holidays',year] }); setSeedConfirm(null); },
    onError:    (e) => { toast.error(e.response?.data?.message||'Failed'); setSeedConfirm(null); },
  });

  const navMonth = (dir) => {
    let m = month+dir, y = year;
    if (m<1) { m=12; y--; } if (m>12) { m=1; y++; }
    setMonth(m); setYear(y);
  };

  // ── Determine if a day is a "weekend" (non-working) ───────────────────────
  // Sunday (0) = weekend unless sundayWorking is ON
  // Saturday (6) = never a weekend (company works Sat)
  const isWeekendDay = (dayOfWeek) => {
    if (dayOfWeek === 0) return !sundayWorking; // Sunday
    return false; // Sat and Mon–Fri are working days
  };

  return (
    <div>
      <PageHeader title="Attendance & Leave" description="Track daily attendance, leave balances, and holidays." />

      <Tabs defaultValue="calendar" className="space-y-5">
        <TabsList className="bg-white border border-slate-200 p-1 h-auto gap-1">
          {[['calendar','Calendar'],['balances','Leave Balances'],['holidays','Holiday Master']].map(([v,l]) => (
            <TabsTrigger key={v} value={v}
              className="data-[state=active]:bg-[#2E6DA4] data-[state=active]:text-white px-4 py-2 text-sm">
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ════════════ CALENDAR ════════════ */}
        <TabsContent value="calendar">
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_280px] gap-5">

            {/* Left — calendar */}
            <div className="space-y-4">
              {/* Controls row */}
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <Select value={selectedId} onValueChange={setEmpId}>
                  <SelectTrigger className="h-10 bg-white border-slate-200 flex-1 max-w-xs">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(e => (
                      <SelectItem key={e._id} value={e._id}>
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-full bg-[#2E6DA4] flex items-center justify-center text-white text-[9px] font-semibold">
                            {e.name.split(' ').map(n=>n[0]).join('').slice(0,2)}
                          </div>
                          <span>{e.name}</span>
                          <span className="text-xs text-slate-400">· {e.department}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={() => navMonth(-1)} className="h-10 w-10">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <div className="w-40 text-center h-10 px-3 rounded-md bg-white border border-slate-200 flex items-center justify-center text-sm font-semibold text-slate-900">
                    {MONTHS[month-1]} {year}
                  </div>
                  <Button variant="outline" size="icon" onClick={() => navMonth(1)} className="h-10 w-10">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>

                <div className="flex items-center gap-2 ml-auto">
                  {/* ── Sunday working toggle ── */}
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-50 border border-slate-200">
                    <Settings2 className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-xs text-slate-600">Sun working</span>
                    <Switch
                      checked={sundayWorking}
                      onCheckedChange={(val) => updateSettingsMut.mutate({ sundayWorking: val })}
                      className="data-[state=checked]:bg-[#2E6DA4]"
                    />
                  </div>

                  {isLocked && (
                    <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
                      <Lock className="w-3 h-3" /> Payroll locked
                    </div>
                  )}
                  <Button onClick={handleSave} disabled={saveMut.isPending || !selectedId || isLocked}
                    className="bg-[#2E6DA4] hover:bg-[#255a88] text-white h-10 px-5">
                    <Save className="w-4 h-4 mr-1.5" />
                    {saveMut.isPending ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </div>

              {/* Calendar grid */}
              <Card className="p-5 border-slate-200 shadow-sm">
                {!selectedId ? (
                  <div className="py-20 text-center text-slate-400 text-sm">Select an employee to view attendance</div>
                ) : isFetching || attLoading ? (
                  <div className="py-20 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin" /> Loading…
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-7 mb-2">
                      {WEEKDAYS.map((d, i) => (
                        <div key={d} className={`text-center text-[11px] uppercase tracking-wider font-medium py-2 ${
                          i === 0 ? (sundayWorking ? 'text-slate-400' : 'text-red-300') : 'text-slate-400'
                        }`}>
                          {d}
                          {i === 0 && sundayWorking && (
                            <span className="ml-0.5 text-[8px] text-emerald-500">✓</span>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-7 gap-1.5">
                      {Array.from({ length: firstWeekday }).map((_,i) => <div key={`blank-${i}`} />)}
                      {Array.from({ length: daysInMonth }).map((_,i) => {
                        const day       = i + 1;
                        const status    = localDays[day] || '';
                        const cfg       = STATUS_CONFIG[status];
                        const holiday   = holidayMap[day];
                        const dayOfWeek = new Date(year,month-1,day).getDay();
                        const isSunday  = dayOfWeek === 0;
                        const weekend   = isWeekendDay(dayOfWeek);
                        const isToday   = day===now.getDate() && month===now.getMonth()+1 && year===now.getFullYear();
                        const cellDate  = new Date(year,month-1,day);
                        const todayMid  = new Date(); todayMid.setHours(0,0,0,0);
                        const isPast    = cellDate < todayMid;
                        const hasOT     = (overtime[day]||0) > 0;
                        const leaveType = leaveTypes[day] || '';
                        // Sunday is clickable if sundayWorking is ON
                        const isClickable = (!isPast || !!status) && (!weekend || sundayWorking || !!status);

                        // Holiday-only cell (no status marked)
                        if (holiday && !status) {
                          const ts = TYPE_STYLE[holiday.type] || TYPE_STYLE.company;
                          return (
                            <div key={day}
                              className="aspect-square rounded-xl flex flex-col items-center justify-center bg-violet-50 border border-violet-200 relative select-none"
                              title={holiday.name}
                            >
                              <span className="text-sm font-semibold text-violet-700">{day}</span>
                              <span className={`w-1.5 h-1.5 rounded-full mt-0.5 ${ts.dot}`} />
                              <span className="text-[8px] text-violet-500 leading-none mt-0.5 truncate w-full text-center px-1">
                                {holiday.name.length > 7 ? holiday.name.slice(0,6)+'…' : holiday.name}
                              </span>
                            </div>
                          );
                        }

                        return (
                          <button key={day}
                            onClick={() => isClickable && !isLocked && openDay(day)}
                            disabled={(!isClickable && !status) || isLocked}
                            title={
                              isSunday && !sundayWorking ? 'Sunday — enable "Sun working" to mark attendance' :
                              isLocked ? 'Attendance locked — payroll already run' :
                              status === 'leave' && leaveType ? `${STATUS_CONFIG.leave.label} · ${leaveType}` :
                              cfg?.label || 'Click to mark'
                            }
                            className={[
                              'aspect-square rounded-xl flex flex-col items-center justify-center relative transition-all duration-100 select-none',
                              status ? '' : 'border',
                              status
                                ? `${cfg.bg} text-white shadow-sm`
                                : weekend
                                  ? 'bg-slate-50 border-slate-100 text-slate-300'
                                  : 'bg-white border-slate-200 text-slate-700',
                              // Sunday working highlight when enabled but no status
                              isSunday && sundayWorking && !status ? 'border-emerald-200 bg-emerald-50/30' : '',
                              isToday && !status ? 'ring-2 ring-[#2E6DA4] ring-offset-1' : '',
                              isPast && !status ? 'opacity-30' : '',
                              isClickable && !isLocked ? 'hover:scale-105 active:scale-95 cursor-pointer' : 'cursor-default',
                            ].join(' ')}
                          >
                            <span className="text-sm font-bold leading-none">{day}</span>

                            {/* Status short label — for leave show leave type (PL/CL/SL/LOP) not just L */}
                            {status && status !== 'leave' && (
                              <span className="text-[9px] font-bold uppercase tracking-widest leading-none mt-0.5 opacity-90">
                                {cfg.short}
                              </span>
                            )}
                            {status === 'leave' && (
                              <span className={`text-[8px] font-bold uppercase leading-none mt-0.5 ${
                                leaveType ? LEAVE_TYPE_COLOR[leaveType] || 'opacity-90' : 'opacity-90'
                              }`}>
                                {leaveType ? LEAVE_TYPE_SHORT[leaveType] : 'L'}
                              </span>
                            )}

                            {/* Holiday dot overlay */}
                            {holiday && status && (
                              <span className={`absolute top-1 right-1 w-1.5 h-1.5 rounded-full ${TYPE_STYLE[holiday.type]?.dot}`} />
                            )}

                            {/* Overtime badge — show on any status including sunday */}
                            {hasOT && (
                              <span className="absolute bottom-0.5 right-0.5 text-[7px] font-mono font-bold opacity-90 leading-none">
                                +{overtime[day]}h
                              </span>
                            )}

                            {/* Today dot */}
                            {isToday && !status && (
                              <span className="absolute bottom-1 w-1 h-1 rounded-full bg-[#2E6DA4]" />
                            )}

                            {/* Lock overlay */}
                            {isLocked && status && (
                              <span className="absolute top-0.5 left-0.5">
                                <Lock className="w-2 h-2 opacity-40" />
                              </span>
                            )}

                            {/* Sunday indicator when working but no status yet */}
                            {isSunday && sundayWorking && !status && !isPast && (
                              <span className="text-[8px] text-emerald-500 leading-none mt-0.5">Sun</span>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {Object.keys(holidayMap).length > 0 && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">Holidays this month</div>
                        <div className="flex flex-wrap gap-1.5">
                          {Object.values(holidayMap).map((h,i) => {
                            const ts = TYPE_STYLE[h.type] || TYPE_STYLE.company;
                            return (
                              <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${ts.cls}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${ts.dot}`} />
                                {new Date(h.date).getDate()} — {h.name}
                                {!h.isMandatory && <span className="opacity-50">(opt)</span>}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Card>

              {/* Legend */}
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="text-slate-400">Click a day to mark:</span>
                {Object.entries(STATUS_CONFIG).map(([k,v]) => (
                  <span key={k} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border ${v.light}`}>
                    <span className={`w-2 h-2 rounded-sm ${v.bg}`} />{v.label}
                  </span>
                ))}
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border bg-violet-50 text-violet-700 border-violet-200">
                  <span className="w-2 h-2 rounded-full bg-violet-400" />Holiday
                </span>
                {sundayWorking && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border bg-emerald-50 text-emerald-700 border-emerald-200">
                    <span className="w-2 h-2 rounded-full bg-emerald-400" />Sunday (working)
                  </span>
                )}
                {isLocked && (
                  <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                    <Lock className="w-3 h-3" /> Locked — payroll run
                  </span>
                )}
              </div>
            </div>

            {/* Right — sidebar */}
            <div className="space-y-4">
              {selectedEmp && (
                <Card className="p-4 border-slate-200 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#2E6DA4] to-[#1E3A5F] flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                      {selectedEmp.name.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{selectedEmp.name}</div>
                      <div className="text-xs text-slate-500">{selectedEmp.department} · {selectedEmp.employeeCode}</div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500 flex justify-between">
                    <span>{MONTHS[month-1]} {year}</span>
                    <span className="font-mono">{formatINR(selectedEmp.salaryStructure?.grossFixed||0)}/mo</span>
                  </div>
                </Card>
              )}

              <Card className="p-4 border-slate-200 shadow-sm">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Monthly Summary</div>
                <div className="space-y-2">
                  {[
                    { label: 'Present',        value: summary.present,              color: 'text-emerald-600', bg: 'bg-emerald-50' },
                    { label: 'Absent / LOP',   value: summary.absent,               color: 'text-red-600',     bg: 'bg-red-50'     },
                    { label: 'Half Days',      value: summary['half-day'],          color: 'text-amber-600',   bg: 'bg-amber-50'   },
                    { label: 'Leave (paid)',   value: summary.leave,                color: 'text-blue-600',    bg: 'bg-blue-50'    },
                    { label: 'Overtime hrs',   value: `${summary.overtime}h`,       color: 'text-violet-600',  bg: 'bg-violet-50'  },
                    { label: 'Effective Days', value: summary.effective.toFixed(1), color: 'text-[#2E6DA4]',  bg: 'bg-slate-50'   },
                  ].map(row => (
                    <div key={row.label} className={`flex items-center justify-between px-3 py-2 rounded-lg ${row.bg}`}>
                      <span className="text-xs text-slate-600">{row.label}</span>
                      <span className={`text-base font-bold font-mono ${row.color}`}>{row.value}</span>
                    </div>
                  ))}
                </div>

                {summary.absent > 0 && selectedEmp && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Estimated LOP Deduction</div>
                    <div className="text-lg font-bold font-mono text-red-600">
                      −{formatINR((selectedEmp.salaryStructure?.grossFixed||0) / 26 * summary.absent)}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      {formatINR((selectedEmp.salaryStructure?.grossFixed||0)/26)}/day × {summary.absent} absent day{summary.absent!==1?'s':''}
                    </div>
                  </div>
                )}
              </Card>

              <div className="flex items-start gap-2 p-3 rounded-lg bg-slate-50 border border-slate-200">
                <Info className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Half-days deduct 0.5× daily rate. Absent days deduct full daily rate. Leave (PL/CL/SL) is paid — no deduction.
                  {sundayWorking && ' Sunday is a working day — attendance can be marked.'}
                </p>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ════════════ LEAVE BALANCES ════════════ */}
        <TabsContent value="balances">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
                <SelectTrigger className="h-9 w-32 bg-white border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent>{[2024,2025,2026].map(y=><SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
              <span className="text-xs text-slate-400">Auto-updates when attendance is saved</span>
            </div>
            {leaveLoading ? (
              <Card className="p-10 text-center border-slate-200"><RefreshCw className="w-5 h-5 animate-spin text-slate-300 mx-auto" /></Card>
            ) : leaveBalances.length === 0 ? (
              <Card className="p-12 border-dashed border-slate-200 text-center">
                <CalendarIcon className="w-8 h-8 text-slate-200 mx-auto mb-3" />
                <div className="text-sm text-slate-500">No leave data for {year}</div>
                <div className="text-xs text-slate-400 mt-1">Save attendance to auto-create leave records</div>
              </Card>
            ) : (
              <>
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { label:'Employees',  value:leaveBalances.length, color:'text-slate-900',   bg:'bg-white'      },
                    { label:'Avg PL Left',value:(leaveBalances.reduce((s,l)=>s+(l.PL?.remaining||0),0)/leaveBalances.length).toFixed(1), color:'text-emerald-600',bg:'bg-emerald-50'},
                    { label:'Avg CL Left',value:(leaveBalances.reduce((s,l)=>s+(l.CL?.remaining||0),0)/leaveBalances.length).toFixed(1), color:'text-blue-600',   bg:'bg-blue-50'  },
                    { label:'Total LOP',  value:leaveBalances.reduce((s,l)=>s+(l.LOP?.used||0),0)+' days', color:'text-red-600', bg:'bg-red-50'},
                  ].map(c => (
                    <Card key={c.label} className={`p-4 border-slate-200 ${c.bg}`}>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">{c.label}</div>
                      <div className={`text-xl font-bold font-mono mt-1 ${c.color}`}>{c.value}</div>
                    </Card>
                  ))}
                </div>
                <Card className="border-slate-200 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Employee</th>
                          {['PL','CL','SL'].map(t => (
                            <React.Fragment key={t}>
                              <th className="px-3 py-3 text-center text-xs font-medium text-slate-500 uppercase">{t} Due</th>
                              <th className="px-3 py-3 text-center text-xs font-medium text-slate-500 uppercase">{t} Used</th>
                              <th className="px-3 py-3 text-center text-xs font-medium text-slate-500 uppercase">{t} Left</th>
                            </React.Fragment>
                          ))}
                          <th className="px-4 py-3 text-center text-xs font-medium text-red-500 uppercase">LOP</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {leaveBalances.map(lb => (
                          <tr key={lb._id} className="hover:bg-slate-50">
                            <td className="px-5 py-3.5">
                              <div className="text-sm font-medium text-slate-900">{lb.employeeId?.name||'—'}</div>
                              <div className="text-xs text-slate-400">{lb.employeeId?.department}</div>
                            </td>
                            {['PL','CL','SL'].map(t => (
                              <React.Fragment key={t}>
                                <td className="px-3 py-3.5 text-center text-xs font-mono text-slate-400">{lb[t]?.entitled??0}</td>
                                <td className="px-3 py-3.5 text-center text-xs font-mono font-semibold text-slate-700">{lb[t]?.used??0}</td>
                                <td className="px-3 py-3.5 text-center">
                                  <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded-full ${
                                    (lb[t]?.remaining??0)===0?'text-red-600 bg-red-50':
                                    (lb[t]?.remaining??0)<=2?'text-amber-600 bg-amber-50':
                                    'text-emerald-600 bg-emerald-50'}`}>{lb[t]?.remaining??0}</span>
                                </td>
                              </React.Fragment>
                            ))}
                            <td className="px-4 py-3.5 text-center">
                              <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded-full ${(lb.LOP?.used??0)>0?'text-red-600 bg-red-50':'text-slate-300 bg-slate-50'}`}>
                                {lb.LOP?.used??0}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </>
            )}
          </div>
        </TabsContent>

        {/* ════════════ HOLIDAY MASTER ════════════ */}
        <TabsContent value="holidays">
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="text-sm text-slate-500">
                {holidays.length} holidays ·{' '}
                <span className="text-blue-600">{holidays.filter(h=>h.type==='national').length} national</span> ·{' '}
                <span className="text-purple-600">{holidays.filter(h=>h.type==='regional').length} state</span> ·{' '}
                <span className="text-slate-500">{holidays.filter(h=>h.type==='company').length} company</span>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={()=>setSeedConfirm('national')} disabled={seedMut.isPending} className="h-8 text-xs border-blue-200 text-blue-700 hover:bg-blue-50">
                  <Zap className="w-3.5 h-3.5 mr-1" /> National
                </Button>
                <Button variant="outline" size="sm" onClick={()=>setSeedConfirm('state')} disabled={seedMut.isPending} className="h-8 text-xs border-purple-200 text-purple-700 hover:bg-purple-50">
                  <MapPin className="w-3.5 h-3.5 mr-1" /> {user?.state||'State'}
                </Button>
                <Button size="sm" onClick={()=>setHolModal(true)} className="h-8 text-xs bg-[#2E6DA4] hover:bg-[#255a88] text-white">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add
                </Button>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 rounded-lg bg-slate-50 border border-slate-200">
              <Info className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-slate-500">
                <strong className="text-slate-700">Mandatory</strong> — office closed, employees get the day off automatically.{' '}
                <strong className="text-slate-700">Optional</strong> — employees choose; notify them via the bell button.
              </p>
            </div>
            {holLoading ? (
              <Card className="p-10 text-center"><RefreshCw className="w-5 h-5 animate-spin text-slate-300 mx-auto" /></Card>
            ) : holidays.length === 0 ? (
              <Card className="p-12 border-dashed border-slate-200 text-center">
                <CalendarIcon className="w-8 h-8 text-slate-200 mx-auto mb-3" />
                <div className="text-sm text-slate-500 mb-3">No holidays for {year}</div>
                <div className="flex justify-center gap-2">
                  <Button variant="outline" size="sm" onClick={()=>setSeedConfirm('national')} className="text-xs border-blue-200 text-blue-700 hover:bg-blue-50"><Zap className="w-3 h-3 mr-1" /> Import National</Button>
                  <Button variant="outline" size="sm" onClick={()=>setSeedConfirm('state')} className="text-xs border-purple-200 text-purple-700 hover:bg-purple-50"><MapPin className="w-3 h-3 mr-1" /> Import {user?.state}</Button>
                </div>
              </Card>
            ) : (
              <Card className="border-slate-200 shadow-sm overflow-hidden">
                {['national','regional','company'].map(type => {
                  const group = holidays.filter(h=>h.type===type).sort((a,b)=>new Date(a.date)-new Date(b.date));
                  if (!group.length) return null;
                  const ts = TYPE_STYLE[type];
                  return (
                    <div key={type}>
                      <div className="flex items-center gap-2 px-5 py-2.5 bg-slate-50 border-b border-slate-100">
                        <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${ts.cls}`}>{ts.label}</span>
                        <span className="text-xs text-slate-400">{group.length} holiday{group.length!==1?'s':''}</span>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {group.map(h => (
                          <div key={h._id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50">
                            <div className="w-10 h-10 rounded-xl bg-slate-100 flex flex-col items-center justify-center flex-shrink-0">
                              <span className="text-[9px] font-medium text-slate-400 uppercase leading-none">{new Date(h.date).toLocaleDateString('en-IN',{month:'short'})}</span>
                              <span className="text-sm font-bold text-slate-900 leading-none">{new Date(h.date).getDate()}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-slate-900">{h.name}</span>
                                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase ${h.isMandatory?'bg-emerald-100 text-emerald-700':'bg-amber-100 text-amber-700'}`}>
                                  {h.isMandatory?'Mandatory':'Optional'}
                                </span>
                              </div>
                              <div className="text-xs text-slate-400 mt-0.5">
                                {new Date(h.date).toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long'})}{h.isPaid?' · Paid':' · Unpaid'}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-[10px] text-slate-400">Opt</span>
                              <Switch checked={!!h.isMandatory} onCheckedChange={val => h._id && toggleMandMut.mutate({ id:h._id, val })} className="data-[state=checked]:bg-emerald-500" />
                              <span className="text-[10px] text-slate-400">Req</span>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <Button variant="ghost" size="sm" onClick={()=>setNotifModal(h)} className="h-8 w-8 p-0 text-slate-400 hover:text-[#2E6DA4]"><Bell className="w-3.5 h-3.5" /></Button>
                              <Button variant="ghost" size="sm" onClick={()=>removeHolMut.mutate(new Date(h.date).toISOString().slice(0,10))} className="h-8 w-8 p-0 text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* ── Day Edit Modal ── */}
      <Dialog open={!!dayModal} onOpenChange={() => setDayModal(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">{MONTHS[month-1]} {dayModal?.day}, {year}</DialogTitle>
            <DialogDescription>Set status, leave type, and overtime for this day.</DialogDescription>
          </DialogHeader>
          {dayModal && (
            <div className="space-y-4 pt-1">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Status</Label>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(STATUS_CONFIG).map(([k,v]) => (
                    <button key={k}
                      onClick={() => setDayModal({...dayModal, status: dayModal.status===k ? '' : k, leaveType: k==='leave' ? dayModal.leaveType : ''})}
                      className={['py-2.5 rounded-xl text-center text-xs font-semibold border-2 transition-all',
                        dayModal.status===k ? `${v.bg} text-white border-transparent shadow-md scale-105` : `bg-white ${v.light} hover:scale-102`
                      ].join(' ')}>
                      {v.short}<br/><span className="text-[9px] font-normal">{v.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Leave type — always show when status is leave */}
              {dayModal.status === 'leave' && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">Leave Type</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {LEAVE_TYPES.map(lt => (
                      <button key={lt.value}
                        onClick={() => setDayModal({...dayModal, leaveType: lt.value})}
                        className={['py-2 px-3 rounded-lg text-xs font-semibold border-2 transition-all text-left',
                          dayModal.leaveType===lt.value ? 'bg-blue-500 text-white border-transparent' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'
                        ].join(' ')}>
                        <div className="font-bold">{lt.value}</div>
                        <div className="text-[9px] opacity-80 font-normal">{lt.sub}</div>
                      </button>
                    ))}
                  </div>
                  {dayModal.status === 'leave' && !dayModal.leaveType && (
                    <p className="text-[10px] text-amber-600 mt-1.5">Please select a leave type</p>
                  )}
                </div>
              )}

              {/* Overtime — available for present AND for sunday working */}
              {(dayModal.status === 'present' || (dayModal.status === '' && sundayWorking && new Date(year,month-1,dayModal.day).getDay()===0)) && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">Overtime Hours</Label>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setDayModal({...dayModal, overtime: Math.max(0,(dayModal.overtime||0)-0.5)})}
                      className="w-9 h-9 rounded-lg border border-slate-200 text-slate-600 font-bold hover:bg-slate-50 flex items-center justify-center">−</button>
                    <div className="flex-1 text-center font-mono text-xl font-bold text-slate-900">{(dayModal.overtime||0).toFixed(1)}h</div>
                    <button onClick={() => setDayModal({...dayModal, overtime: (dayModal.overtime||0)+0.5})}
                      className="w-9 h-9 rounded-lg border border-slate-200 text-slate-600 font-bold hover:bg-slate-50 flex items-center justify-center">+</button>
                  </div>
                  <p className="text-[10px] text-slate-400 text-center mt-1.5">Paid at configured overtime multiplier</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDayModal(null)}>Cancel</Button>
            <Button onClick={saveDay} disabled={saveMut.isPending || (dayModal?.status==='leave' && !dayModal?.leaveType)}
              className="bg-[#2E6DA4] hover:bg-[#255a88] text-white">
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add Holiday Modal ── */}
      <Dialog open={holModal} onOpenChange={setHolModal}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Add Holiday</DialogTitle><DialogDescription>Add a custom holiday to the calendar</DialogDescription></DialogHeader>
          <div className="space-y-4 pt-1">
            <div><Label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">Name</Label><Input value={newHol.name} onChange={e=>setNewHol({...newHol,name:e.target.value})} placeholder="e.g., Company Anniversary" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">Date</Label><Input type="date" value={newHol.date} onChange={e=>setNewHol({...newHol,date:e.target.value})} /></div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">Type</Label>
              <Select value={newHol.type} onValueChange={v=>setNewHol({...newHol,type:v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="company">Company</SelectItem><SelectItem value="national">National</SelectItem><SelectItem value="regional">State / Regional</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
              <div><div className="text-sm font-medium text-slate-900">Mandatory</div><div className="text-xs text-slate-400">Office closed — all employees off</div></div>
              <Switch checked={newHol.isMandatory} onCheckedChange={v=>setNewHol({...newHol,isMandatory:v})} className="data-[state=checked]:bg-emerald-500" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setHolModal(false)}>Cancel</Button>
            <Button className="bg-[#2E6DA4] hover:bg-[#255a88] text-white" disabled={addHolMut.isPending}
              onClick={()=>{ if(!newHol.name||!newHol.date){toast.error('Fill name and date');return;} addHolMut.mutate(newHol); }}>
              {addHolMut.isPending?'Adding…':'Add Holiday'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Seed Confirm ── */}
      <Dialog open={!!seedConfirm} onOpenChange={()=>setSeedConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {seedConfirm==='national'?<><Zap className="w-5 h-5 text-blue-600"/>Import National</>:<><MapPin className="w-5 h-5 text-purple-600"/>Import {user?.state}</>}
            </DialogTitle>
            <DialogDescription>{seedConfirm==='national'?`Adds all India national holidays for ${year}.`:`Adds ${user?.state||'state'}-specific holidays for ${year}.`}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setSeedConfirm(null)}>Cancel</Button>
            <Button disabled={seedMut.isPending} onClick={()=>seedMut.mutate(seedConfirm)}
              className={seedConfirm==='national'?'bg-blue-600 hover:bg-blue-700 text-white':'bg-purple-600 hover:bg-purple-700 text-white'}>
              {seedMut.isPending?'Importing…':'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Notify Modal ── */}
      <Dialog open={!!notifModal} onOpenChange={()=>setNotifModal(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Bell className="w-5 h-5 text-[#2E6DA4]" /> Notify Employees</DialogTitle></DialogHeader>
          {notifModal && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                <div className="text-sm font-semibold text-slate-900">{notifModal.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{new Date(notifModal.date).toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</div>
              </div>
              <div className="p-3 rounded-xl bg-blue-50 border border-blue-100 text-xs text-slate-700 whitespace-pre-line font-mono leading-relaxed">
                {`🎉 ${notifModal.name}\n📅 ${new Date(notifModal.date).toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long'})}\n${notifModal.isMandatory?'✅ Mandatory — office closed.':'📋 Optional — employees may take leave.'}`}
              </div>
              <Button onClick={() => { addNotification(`🎉 ${notifModal.name}`); toast.success('Notification sent'); setNotifModal(null); }}
                className="bg-[#2E6DA4] hover:bg-[#255a88] text-white w-full">
                <Bell className="w-4 h-4 mr-2" /> Send In-App Notification
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" disabled className="flex-1 text-xs text-slate-400">📱 WhatsApp (soon)</Button>
                <Button variant="outline" disabled className="flex-1 text-xs text-slate-400">💬 SMS (soon)</Button>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={()=>setNotifModal(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}